<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { message as ElMessage } from '../../utils/message';
import api from '../../api';
import SettingsSidebar from '../settings/SettingsSidebar.vue';

const router = useRouter();
const name   = ref('');
const email  = ref('');
const role   = ref('user');
const loading = ref(false);
const error  = ref('');

const submit = async () => {
  if (!name.value || !email.value) { error.value = 'Name and email are required.'; return; }
  loading.value = true;
  error.value   = '';
  try {
    await api.post('customer/users/invite', { name: name.value, email: email.value, role: role.value });
    ElMessage.success('Invitation sent!');
    router.push('/users');
  } catch (err: any) {
    error.value = err.response?.data?.message || 'Failed to send invitation.';
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div class="st-page" v-loading="loading">

    <div class="st-header">
      <div class="st-inner">
        <h1 class="st-title">Settings</h1>
        <p class="st-subtitle">Manage your account and business preferences</p>
      </div>
      <div class="st-header-rule"></div>
    </div>

    <div class="st-inner st-body">
      <SettingsSidebar />

      <div class="st-content">

        <!-- Content header -->
        <div class="st-content-head">
          <router-link to="/business" class="st-back">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
            Team Members
          </router-link>
          <div class="st-content-title">Invite a User</div>
          <div class="st-content-sub">They'll receive an email to join your account</div>
        </div>

        <!-- Invite form card -->
        <div class="st-card">
          <div class="st-card-band">
            <span class="st-card-band-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0061eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
            </span>
            <span class="st-card-band-label">Team Invitation</span>
          </div>

          <div class="st-card-body">
            <div class="st-error" v-if="error">{{ error }}</div>

            <div class="st-field">
              <label class="st-label">Name <span class="st-optional">Optional</span></label>
              <input v-model="name" class="st-input" placeholder="Full name" />
            </div>

            <div class="st-field">
              <label class="st-label">Email Address <span class="st-req">*</span></label>
              <input v-model="email" class="st-input" type="email" placeholder="user@company.com" />
            </div>

            <div class="st-actions">
              <button class="st-btn-ghost" @click.prevent="router.push('/users')" :disabled="loading">Cancel</button>
              <button class="st-btn" @click.prevent="submit" :disabled="loading">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                Send Invitation
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
* { box-sizing: border-box; }

.st-page { font-family: 'DM Sans', sans-serif; background: white; min-height: 100%; }
.st-inner { max-width: 1280px; margin: 0 auto; padding: 0 32px; }

.st-header { background: white; padding: 24px 0 0; }
.st-header .st-inner { padding-bottom: 18px; }
.st-title { font-size: 1.4rem; font-weight: 700; color: #0f1729; margin: 0 0 3px; }
.st-subtitle { font-size: 0.83rem; color: #94a3b8; margin: 0; }
.st-header-rule { height: 1.5px; background: #e8f0ff; }

.st-body { display: flex; gap: 24px; padding-top: 28px; padding-bottom: 40px; align-items: flex-start; }
.st-content { flex: 1; min-width: 0; }

/* Content header */
.st-content-head { margin-bottom: 16px; }
.st-back {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 0.78rem; font-weight: 600; color: #0061eb;
  text-decoration: none; margin-bottom: 10px;
  transition: gap 0.15s, opacity 0.15s; opacity: 0.85;
}
.st-back:hover { gap: 8px; opacity: 1; }
.st-content-title { font-size: 1rem; font-weight: 700; color: #0f1729; margin-bottom: 2px; }
.st-content-sub { font-size: 0.78rem; color: #94a3b8; }

/* Card */
.st-card { background: white; border: 1.5px solid #ccdeff; border-radius: 12px; overflow: hidden; max-width: 560px; }
.st-card-band {
  display: flex; align-items: center; gap: 9px;
  background: #f0f6ff; border-bottom: 1.5px solid #ccdeff; padding: 12px 24px;
}
.st-card-band-icon { display: flex; align-items: center; }
.st-card-band-label {
  font-size: 0.68rem; font-weight: 700; color: #6b86b8;
  text-transform: uppercase; letter-spacing: 0.08em;
}
.st-card-body { padding: 24px; }

.st-error {
  background: #fef2f2; border: 1.5px solid #fca5a5; color: #dc2626;
  border-radius: 8px; padding: 10px 14px; font-size: 0.82rem; font-weight: 500; margin-bottom: 18px;
}

.st-field { display: flex; flex-direction: column; margin-bottom: 16px; }
.st-label { font-size: 0.78rem; font-weight: 600; color: #475569; margin-bottom: 6px; }
.st-optional { font-weight: 400; color: #94a3b8; font-size: 0.73rem; }
.st-req { color: #dc2626; }
.st-input {
  width: 100%; height: 42px; padding: 0 14px;
  border: 1.5px solid #e2e8f0; border-radius: 9px;
  font-family: 'DM Sans', sans-serif; font-size: 0.87rem; color: #1e293b;
  outline: none; transition: border-color 0.15s, box-shadow 0.15s; background: white;
}
.st-input:focus { border-color: #0061eb; box-shadow: 0 0 0 3px rgba(0,97,235,0.08); }
.st-input::placeholder { color: #94a3b8; }

.st-actions {
  display: flex; align-items: center; justify-content: flex-end; gap: 10px;
  margin-top: 24px; padding-top: 18px; border-top: 1px solid #f1f5f9;
}
.st-btn {
  height: 42px; padding: 0 24px;
  background: #0061eb; color: white; border: none; border-radius: 9px;
  font-family: 'DM Sans', sans-serif; font-size: 0.88rem; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; gap: 8px; transition: background 0.15s;
}
.st-btn:hover:not(:disabled) { background: #0050c7; }
.st-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.st-btn-ghost {
  height: 42px; padding: 0 18px;
  background: white; color: #64748b; border: 1.5px solid #e2e8f0; border-radius: 9px;
  font-family: 'DM Sans', sans-serif; font-size: 0.86rem; font-weight: 600;
  cursor: pointer; transition: border-color 0.15s, color 0.15s;
}
.st-btn-ghost:hover:not(:disabled) { border-color: #ccdeff; color: #0061eb; }
.st-btn-ghost:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
